import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, doublePrecision, timestamp, boolean, jsonb, numeric, index, uniqueIndex, unique, foreignKey, check } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type { InkMixRecord } from "./ink-mix-record";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  forcePasswordChange: boolean("force_password_change").notNull().default(false),
  isSuperAdmin: boolean("is_super_admin").notNull().default(false),
  passwordChangedAt: timestamp("password_changed_at"),
  passwordResetToken: text("password_reset_token"),
  passwordResetExpiry: timestamp("password_reset_expiry"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_users_password_reset_token").on(table.passwordResetToken).where(sql`password_reset_token IS NOT NULL`),
]);

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export const machines = pgTable("machines", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  type: text("type").notNull(),
  color: text("color").notNull().default("#3b82f6"),
  active: boolean("active").notNull().default(true),
  description: text("description"),
  photoPath: text("photo_path"),
  headCount: integer("head_count"),
  sortOrder: integer("sort_order").notNull().default(99),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  check("machines_type_check", sql`${table.type} IN ('screenprint_press', 'embroidery_machine', 'heat_press', 'poly_bagger')`),
  index("idx_machines_active").on(table.active),
  index("idx_machines_sort_order").on(table.sortOrder),
]);

export const insertMachineSchema = createInsertSchema(machines).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertMachine = z.infer<typeof insertMachineSchema>;
export type Machine = typeof machines.$inferSelect;

export const workOrders = pgTable("work_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").unique(),
  orderNumber: text("order_number").notNull(),
  customerName: text("customer_name").notNull(),
  accountManager: text("account_manager"),
  shipDate: timestamp("ship_date"),
  whenComplete: text("when_complete"),
  shippingStreet: text("shipping_street"),
  shippingCity: text("shipping_city"),
  shippingState: text("shipping_state"),
  shippingZip: text("shipping_zip"),
  decorationType: text("decoration_type").notNull(),
  incomingVendorProduct: text("incoming_vendor_product"),
  totalProductQuantity: integer("total_product_quantity").default(0),
  productName: text("product_name").notNull(),
  productSku: text("product_sku"),
  quantity: integer("quantity").notNull().default(1),
  garmentType: text("garment_type"),
  garmentColor: text("garment_color"),
  colors: integer("colors").default(1),
  location: text("location"),
  specialInstructions: text("special_instructions"),
  status: text("status").notNull().default("pending"),
  priority: text("priority").notNull().default("normal"),
  dueDate: timestamp("due_date"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  needsPolyBagging: boolean("needs_poly_bagging").notNull().default(false),
  polyBagStickerSku: text("poly_bag_sticker_sku"),
  polyBagStickerName: text("poly_bag_sticker_name"),
  grandTotal: numeric("grand_total", { precision: 12, scale: 2 }),
  vendorNumber: text("vendor_number"),
  timeEstimate: numeric("time_estimate", { precision: 6, scale: 2 }),
  stitchCount: integer("stitch_count"),
  embroideryQuantity: integer("embroidery_quantity"),
  impressions: integer("impressions"),
  screenprintQuantity: integer("screenprint_quantity"),
  heatpressQuantity: integer("heatpress_quantity"),
  mitigatingFactors: integer("mitigating_factors"),
  mitigatingFactorsNotes: text("mitigating_factors_notes"),
  polybaggingQuantity: integer("polybagging_quantity"),
  workdriveUrl: text("workdrive_url"),
  productionNotes: text("production_notes"),
  overrideExplanation: text("override_explanation"),
  customerContact: text("customer_contact"),
  carrier: text("carrier"),
  version: integer("version").notNull().default(1),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_work_orders_status").on(table.status),
  index("idx_work_orders_decoration_type").on(table.decorationType),
  index("idx_work_orders_zoho_id").on(table.zohoId),
  index("idx_work_orders_order_number").on(table.orderNumber),
  index("idx_work_orders_created_at").on(table.createdAt),
  index("idx_work_orders_active_status").on(table.status).where(sql`deleted_at IS NULL`),
  index("idx_work_orders_active_deco").on(table.decorationType).where(sql`deleted_at IS NULL`),
  index("idx_work_orders_active_ship_date").on(table.shipDate).where(sql`deleted_at IS NULL`),
  uniqueIndex("uq_work_orders_order_number_active").on(table.orderNumber).where(sql`deleted_at IS NULL`),
  check("work_orders_status_check", sql`${table.status} IN ('pending', 'scheduled', 'ready', 'packing', 'complete', 'archived')`),
  check("work_orders_priority_check", sql`${table.priority} IN ('low', 'normal', 'high', 'firm', 'rush')`),
  check("work_orders_decoration_type_check", sql`${table.decorationType} IN ('screenprint', 'embroidery', 'heat_press', 'poly_bagging', 'undetermined')`),
]);

export const insertWorkOrderSchema = createInsertSchema(workOrders).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertWorkOrder = z.infer<typeof insertWorkOrderSchema>;
export type WorkOrder = typeof workOrders.$inferSelect;

// HUB-508: source goods are distinct from the run-level REPRINT relationship.
export const workOrderSourceLinks = pgTable("work_order_source_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull(),
  sourceWorkOrderId: varchar("source_work_order_id"),
  workOrderProductId: varchar("work_order_product_id"),
  sourceKind: text("source_kind"),
  fulfillmentOrderNumber: text("fulfillment_order_number"),
  externalReference: text("external_reference"),
  isOutsideVendor: boolean("is_outside_vendor").notNull().default(false),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  createdByUserId: varchar("created_by_user_id").notNull(),
  lastUpdatedByUserId: varchar("last_updated_by_user_id").notNull(),
  deletedByUserId: varchar("deleted_by_user_id"),
}, (table) => [
  check("work_order_source_links_kind_check", sql`COALESCE(
    (${table.sourceKind} IS NULL AND ${table.workOrderProductId} IS NULL AND ${table.fulfillmentOrderNumber} IS NULL AND ${table.externalReference} IS NULL
      AND ((${table.isOutsideVendor} AND ${table.sourceWorkOrderId} IS NULL) OR (NOT ${table.isOutsideVendor} AND ${table.sourceWorkOrderId} IS NOT NULL)))
    OR (${table.workOrderProductId} IS NOT NULL AND (
      (${table.sourceKind} = 'internal' AND NOT ${table.isOutsideVendor} AND ${table.sourceWorkOrderId} IS NOT NULL AND ${table.fulfillmentOrderNumber} IS NULL AND ${table.externalReference} IS NULL)
      OR (${table.sourceKind} = 'fulfillment' AND NOT ${table.isOutsideVendor} AND ${table.sourceWorkOrderId} IS NULL AND ${table.externalReference} IS NULL
        AND ${table.fulfillmentOrderNumber} = btrim(${table.fulfillmentOrderNumber}) AND length(${table.fulfillmentOrderNumber}) BETWEEN 1 AND 120)
      OR (${table.sourceKind} = 'external' AND ${table.isOutsideVendor} AND ${table.sourceWorkOrderId} IS NULL AND ${table.fulfillmentOrderNumber} IS NULL
        AND (${table.externalReference} IS NULL OR (${table.externalReference} = btrim(${table.externalReference}) AND length(${table.externalReference}) BETWEEN 1 AND 200)))
    )), false)`),
  check("work_order_source_links_not_self_check", sql`${table.sourceWorkOrderId} IS NULL OR ${table.sourceWorkOrderId} <> ${table.workOrderId}`),
  check("work_order_source_links_version_check", sql`${table.version} > 0`),
  foreignKey({ name: "work_order_source_links_product_fk", columns: [table.workOrderProductId], foreignColumns: [workOrderProducts.id] }).onDelete("restrict").onUpdate("no action"),
  foreignKey({ name: "work_order_source_links_work_order_fk", columns: [table.workOrderId], foreignColumns: [workOrders.id] }).onDelete("cascade").onUpdate("no action"),
  foreignKey({ name: "work_order_source_links_source_work_order_fk", columns: [table.sourceWorkOrderId], foreignColumns: [workOrders.id] }).onDelete("restrict").onUpdate("no action"),
  foreignKey({ name: "work_order_source_links_created_by_fk", columns: [table.createdByUserId], foreignColumns: [users.id] }).onDelete("restrict").onUpdate("no action"),
  foreignKey({ name: "work_order_source_links_last_updated_by_fk", columns: [table.lastUpdatedByUserId], foreignColumns: [users.id] }).onDelete("restrict").onUpdate("no action"),
  foreignKey({ name: "work_order_source_links_deleted_by_fk", columns: [table.deletedByUserId], foreignColumns: [users.id] }).onDelete("restrict").onUpdate("no action"),
  uniqueIndex("uq_work_order_source_links_active_source").on(table.workOrderId, table.sourceWorkOrderId).where(sql`${table.deletedAt} IS NULL AND ${table.sourceKind} IS NULL AND NOT ${table.isOutsideVendor}`),
  uniqueIndex("uq_work_order_source_links_active_vendor").on(table.workOrderId).where(sql`${table.deletedAt} IS NULL AND ${table.sourceKind} IS NULL AND ${table.isOutsideVendor}`),
  uniqueIndex("uq_work_order_source_links_active_product").on(table.workOrderProductId).where(sql`${table.deletedAt} IS NULL AND ${table.sourceKind} IS NOT NULL`),
  index("idx_work_order_source_links_product").on(table.workOrderProductId),
  index("idx_work_order_source_links_work_order").on(table.workOrderId),
  index("idx_work_order_source_links_source").on(table.sourceWorkOrderId),
  index("idx_work_order_source_links_deleted_at").on(table.deletedAt),
  index("idx_work_order_source_links_created_by").on(table.createdByUserId),
  index("idx_work_order_source_links_last_updated_by").on(table.lastUpdatedByUserId),
  index("idx_work_order_source_links_deleted_by").on(table.deletedByUserId),
]);

export const insertWorkOrderSourceLinkSchema = createInsertSchema(workOrderSourceLinks).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
  createdByUserId: true, lastUpdatedByUserId: true, deletedByUserId: true,
});
export type WorkOrderSourceLink = typeof workOrderSourceLinks.$inferSelect;

export type WorkOrderSplitLineageMovedLine = {
  entityType: "run" | "product";
  id: string;
  canonicalFingerprint: string | null;
  groupSuffix: string;
  snapshot: Record<string, unknown>;
};

// HUB-540: mutable allocation state is separate from immutable split snapshots.
export type WorkOrderSplitProductMember = {
  workOrderId: string;
  // Null means this member has never had a positive share/product row.
  productId: string | null;
};

export type WorkOrderSplitProductDivision = {
  sourceProductId: string;
  canonicalFingerprint: string;
  originalQuantity: number;
  // Null means a manager accepted upstream deletion; it is not quantity zero.
  upstreamQuantity: number | null;
  upstreamRevisionId: string | null;
  members: WorkOrderSplitProductMember[];
};

export type WorkOrderSplitSharedRun = {
  runId: string;
  // Explicit original product coverage, not every product added in the future.
  sourceProductIds: string[];
};

export type WorkOrderSplitState = {
  formatVersion: 1;
  dividedProducts: WorkOrderSplitProductDivision[];
  sharedRuns: WorkOrderSplitSharedRun[];
};

export type WorkOrderSplitLineageSnapshot = Record<string, unknown>;

export const workOrderSplitLineage = pgTable("work_order_split_lineage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  splitGroupId: varchar("split_group_id").notNull(),
  sourceWorkOrderId: varchar("source_work_order_id").notNull().references(() => workOrders.id),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id),
  role: text("role").notNull(),
  suffix: text("suffix").notNull(),
  originalOrderNumber: text("original_order_number").notNull(),
  orderNumberAfterSplit: text("order_number_after_split").notNull(),
  originalZohoId: text("original_zoho_id"),
  movedRunIds: text("moved_run_ids").array().notNull().default(sql`'{}'::text[]`),
  movedProductIds: text("moved_product_ids").array().notNull().default(sql`'{}'::text[]`),
  movedLineFingerprints: jsonb("moved_line_fingerprints").notNull().$type<WorkOrderSplitLineageMovedLine[]>().default(sql`'[]'::jsonb`),
  snapshot: jsonb("snapshot").notNull().$type<WorkOrderSplitLineageSnapshot>().default(sql`'{}'::jsonb`),
  splitState: jsonb("split_state").$type<WorkOrderSplitState | null>(),
  createdByUserId: varchar("created_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdByName: text("created_by_name").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_work_order_split_lineage_group").on(table.splitGroupId),
  index("idx_work_order_split_lineage_source").on(table.sourceWorkOrderId),
  index("idx_work_order_split_lineage_work_order").on(table.workOrderId),
  uniqueIndex("uq_work_order_split_lineage_active_work_order").on(table.workOrderId).where(sql`deleted_at IS NULL`),
  uniqueIndex("uq_work_order_split_lineage_group_suffix").on(table.splitGroupId, table.suffix).where(sql`deleted_at IS NULL`),
  check("work_order_split_lineage_role_check", sql`${table.role} IN ('source', 'sibling')`),
  check("work_order_split_lineage_split_state_check", sql`
    ${table.splitState} IS NULL OR COALESCE(
      ${table.role} = 'source'
      AND ${table.workOrderId} = ${table.sourceWorkOrderId}
      AND jsonb_typeof(${table.splitState}) = 'object'
      AND ${table.splitState}->'formatVersion' = '1'::jsonb
      AND jsonb_typeof(${table.splitState}->'dividedProducts') = 'array'
      AND jsonb_typeof(${table.splitState}->'sharedRuns') = 'array',
      false
    )
  `),
]);

export const insertWorkOrderSplitLineageSchema = createInsertSchema(workOrderSplitLineage).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertWorkOrderSplitLineage = z.infer<typeof insertWorkOrderSplitLineageSchema>;
export type WorkOrderSplitLineage = typeof workOrderSplitLineage.$inferSelect;

export const pressSetupStationSchema = z.object({
  id: z.string().trim().min(1).max(80),
  type: z.enum(["color", "flash", "cooldown"]),
  colorName: z.string().trim().max(100).nullable().optional(),
  pmsColor: z.string().trim().max(40).nullable().optional(),
  notes: z.string().trim().max(1000).optional().default(""),
}).strict();

export const pressSetupPlanSchema = z.object({
  stations: z.array(pressSetupStationSchema).min(1).max(40),
}).strict();

export type PressSetupPlan = z.infer<typeof pressSetupPlanSchema>;

export const workOrderRuns = pgTable("work_order_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runNumber: integer("run_number").notNull().default(1),
  detailedDecoType: text("detailed_deco_type"),
  location: text("location"),
  products: text("products"),
  runQuantity: integer("run_quantity").default(0),
  unitCost: numeric("unit_cost", { precision: 10, scale: 2 }).default("0"),
  totalCost: numeric("total_cost", { precision: 10, scale: 2 }).default("0"),
  notes: text("notes"),
  pantoneColors: text("pantone_colors").array(),
  transferVendor: varchar("transfer_vendor", { length: 30 }),
  transferDetails: varchar("transfer_details", { length: 60 }),
  transferLength: numeric("transfer_length", { precision: 6, scale: 2 }),
  transferWidth: numeric("transfer_width", { precision: 6, scale: 2 }),
  impressions: integer("impressions"),
  stitchCount: integer("stitch_count"),
  mitigatingFactors: text("mitigating_factors"),
  mitigationMultiplier: numeric("mitigation_multiplier", { precision: 8, scale: 2 }),
  digitizingVendor: text("digitizing_vendor"),
  reprintWorkOrderId: varchar("reprint_work_order_id").references(() => workOrders.id, { onDelete: "set null" }),
  manualTimeEstimate: numeric("manual_time_estimate", { precision: 6, scale: 2 }),
  dims: text("dims"),
  coverage: text("coverage"),
  threadBrandColor: text("thread_brand_color"),
  transferType: text("transfer_type"),
  productionNotes: text("production_notes"),
  pressSetupPlan: jsonb("press_setup_plan").$type<PressSetupPlan | null>(),
  isDsPrint: boolean("is_ds_print").notNull().default(false),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_work_order_runs_wo_id").on(table.workOrderId),
  index("idx_work_order_runs_active_wo").on(table.workOrderId).where(sql`deleted_at IS NULL`),
  index("idx_work_order_runs_digitizing_vendor").on(table.digitizingVendor).where(sql`deleted_at IS NULL AND digitizing_vendor IS NOT NULL`),
]);

export const insertWorkOrderRunSchema = createInsertSchema(workOrderRuns).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true }).extend({
  pressSetupPlan: pressSetupPlanSchema.nullable().optional(),
});
export type InsertWorkOrderRun = z.infer<typeof insertWorkOrderRunSchema>;
export type WorkOrderRun = typeof workOrderRuns.$inferSelect;

// Named "color change" versions within a single screen print run (HUB-391).
// The run itself is the implicit "Original" version (colorChangeId IS NULL on
// its inks/proofs); each row here is an additional version carrying its own
// ink (its own Ink Master task), its own proof, and its own Pantone(s).
// Screens, coverage, and run production steps stay shared at the run level.
export const runColorChanges = pgTable("run_color_changes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => workOrderRuns.id, { onDelete: "cascade" }),
  label: text("label").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_run_color_changes_work_order_id").on(table.workOrderId),
  index("idx_run_color_changes_run_id").on(table.runId),
  index("idx_run_color_changes_active_run").on(table.runId).where(sql`deleted_at IS NULL`),
  // The real guard against two concurrent "Add -> Red" inserts: optimistic
  // locking cannot stop duplicate INSERTs. Precedent: uq_process_milestones_active_identity.
  // Normalize on lower(trim(...)) so " Red" and "Red" collide and the DB (not just Zod) is authoritative.
  uniqueIndex("uq_run_color_changes_active_label")
    .on(table.runId, sql`lower(trim(${table.label}))`)
    .where(sql`deleted_at IS NULL`),
  // Composite-unique target so ink_formulas can FK on (run_id, id) and guarantee a
  // color version's inks belong to the SAME run (run-membership integrity).
  unique("uq_run_color_changes_run_id_id").on(table.runId, table.id),
  check("run_color_changes_label_not_blank", sql`length(trim(${table.label})) > 0`),
  check("run_color_changes_sort_order_nonneg", sql`${table.sortOrder} >= 0`),
]);

// Explicit insert schema (not the bare generated one): label trimmed 1-120,
// notes <= 2000 nullable, sortOrder 0-10000. workOrderId/runId are supplied from
// the route path, never the request body.
export const insertRunColorChangeSchema = createInsertSchema(runColorChanges)
  .omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true })
  .extend({
    label: z.string().trim().min(1).max(120),
    notes: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().min(0).max(10000).optional(),
  });
export type InsertRunColorChange = z.infer<typeof insertRunColorChangeSchema>;
export type RunColorChange = typeof runColorChanges.$inferSelect;

export const workOrderProducts = pgTable("work_order_products", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  productName: text("product_name"),
  productDisplayName: text("product_display_name"),
  productVendor: text("product_vendor"),
  vendorItemNumber: text("vendor_item_number"),
  color: text("color"),
  size: text("size"),
  quantity: integer("quantity").default(0),
  quantityReceived: integer("quantity_received").default(0),
  unitCost: numeric("unit_cost", { precision: 10, scale: 2 }),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_work_order_products_wo_id").on(table.workOrderId),
]);

export const insertWorkOrderProductSchema = createInsertSchema(workOrderProducts).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertWorkOrderProduct = z.infer<typeof insertWorkOrderProductSchema>;
export type WorkOrderProduct = typeof workOrderProducts.$inferSelect;

// HUB-551: immutable shortage decisions are separate from physical product quantities.
export const workOrderShortageApprovals = pgTable("work_order_shortage_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade", onUpdate: "cascade" }),
  reason: text("reason").notNull(),
  acceptanceConfirmed: boolean("acceptance_confirmed").notNull(),
  approvedById: varchar("approved_by_id").notNull().references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  approvedByName: text("approved_by_name").notNull(),
  approvedAt: timestamp("approved_at", { withTimezone: true }).notNull().defaultNow(),
  status: text("status").notNull().default("active"),
  statusReason: text("status_reason"),
  statusChangedById: varchar("status_changed_by_id").references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  statusChangedAt: timestamp("status_changed_at", { withTimezone: true }),
  statusChangedByName: text("status_changed_by_name"),
  requestId: text("request_id").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  createdById: varchar("created_by_id").notNull().references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  lastUpdatedById: varchar("last_updated_by_id").references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  deletedById: varchar("deleted_by_id").references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
}, table => [
  uniqueIndex("uq_shortage_approval_active").on(table.workOrderId).where(sql`${table.status} = 'active' AND ${table.deletedAt} IS NULL`),
  uniqueIndex("uq_shortage_approval_request").on(table.approvedById, table.requestId),
  index("idx_shortage_approval_history").on(table.workOrderId, table.createdAt),
  index("idx_shortage_approval_deleted").on(table.deletedAt),
  index("idx_shortage_approval_status_actor").on(table.statusChangedById),
  index("idx_shortage_approval_creator").on(table.createdById),
  index("idx_shortage_approval_updater").on(table.lastUpdatedById),
  index("idx_shortage_approval_deleter").on(table.deletedById),
  check("shortage_approval_status", sql`${table.status} IN ('active', 'superseded', 'revoked', 'invalidated')`),
  check("shortage_approval_acceptance", sql`${table.acceptanceConfirmed} = true`),
  check("shortage_approval_reason", sql`length(trim(${table.reason})) BETWEEN 1 AND 2000`),
]);
export type WorkOrderShortageApproval = typeof workOrderShortageApprovals.$inferSelect;

export const workOrderShortageApprovalItems = pgTable("work_order_shortage_approval_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  approvalId: varchar("approval_id").notNull().references(() => workOrderShortageApprovals.id, { onDelete: "cascade", onUpdate: "cascade" }),
  productId: varchar("product_id").notNull().references(() => workOrderProducts.id, { onDelete: "restrict", onUpdate: "cascade" }),
  productIdentity: text("product_identity").notNull(),
  sourceContext: text("source_context"),
  orderedQuantity: integer("ordered_quantity").notNull(),
  receivedQuantity: integer("received_quantity").notNull(),
  missingQuantity: integer("missing_quantity").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  createdById: varchar("created_by_id").notNull().references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  lastUpdatedById: varchar("last_updated_by_id").references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
  deletedById: varchar("deleted_by_id").references(() => users.id, { onDelete: "restrict", onUpdate: "cascade" }),
}, table => [
  uniqueIndex("uq_shortage_approval_product").on(table.approvalId, table.productId),
  index("idx_shortage_item_product").on(table.productId),
  index("idx_shortage_item_deleted").on(table.deletedAt),
  index("idx_shortage_item_creator").on(table.createdById),
  index("idx_shortage_item_updater").on(table.lastUpdatedById),
  index("idx_shortage_item_deleter").on(table.deletedById),
  check("shortage_item_quantity", sql`${table.missingQuantity} > 0 AND ${table.missingQuantity} <= ${table.orderedQuantity} AND ${table.receivedQuantity} >= 0`),
]);
export type WorkOrderShortageApprovalItem = typeof workOrderShortageApprovalItems.$inferSelect;

// HUB-430 Phase 2: assigns a quantity of an order garment (work_order_products) to a screenprint
// run's color version. colorChangeId IS NULL = the run's implicit "Original" version (same convention
// as ink_formulas / file_attachments). workOrderId is denormalized (alongside runId) ONLY so the
// work-order soft-delete/restore/bulk cascades stay clean one-liners like ink_formulas — it is NEVER
// used for the over-allocation sum. The over-allocation invariant (sum of active quantities per
// (run, garment) <= the garment's order quantity) is enforced PER-RUN in storage under an advisory
// lock: it is cross-row and concurrency-sensitive, so it cannot be a CHECK. A garment can be on
// multiple runs (front + back print both consume all the shirts), which is exactly why the scope is
// the run, not the work order.
export const runColorChangeGarments = pgTable("run_color_change_garments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => workOrderRuns.id, { onDelete: "cascade" }),
  // null = Original. FK is the composite (run_id, color_change_id) declared below.
  colorChangeId: varchar("color_change_id"),
  workOrderProductId: varchar("work_order_product_id").notNull().references(() => workOrderProducts.id, { onDelete: "cascade" }),
  quantity: integer("quantity").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_rccg_wo_id").on(table.workOrderId),
  index("idx_rccg_run_id").on(table.runId),
  index("idx_rccg_product_id").on(table.workOrderProductId),
  index("idx_rccg_active_run").on(table.runId).where(sql`deleted_at IS NULL`),
  // Serves the composite FK cascade; partial-on-NOT-NULL keeps the mostly-Original (null) rows out.
  index("idx_rccg_color_change_id").on(table.colorChangeId).where(sql`color_change_id IS NOT NULL`),
  // One ACTIVE allocation per (run, garment, version). coalesce so Original (null) collides with
  // itself; optimistic locking cannot stop duplicate INSERTs (HUB-391 lesson — duplicate active rows).
  uniqueIndex("uq_rccg_active_run_product_version")
    .on(table.runId, table.workOrderProductId, sql`coalesce(${table.colorChangeId}, '')`)
    .where(sql`deleted_at IS NULL`),
  // Zero quantity deletes the row instead, so a live allocation is always strictly positive.
  check("run_color_change_garments_quantity_positive", sql`${table.quantity} > 0`),
  // Composite FK: a garment allocation's color version must belong to the SAME run (run-membership
  // integrity), not merely exist. MATCH SIMPLE leaves Original (null) rows unconstrained.
  foreignKey({
    columns: [table.runId, table.colorChangeId],
    foreignColumns: [runColorChanges.runId, runColorChanges.id],
    name: "run_color_change_garments_run_color_change_fk",
  }).onDelete("cascade"),
]);

export const insertRunColorChangeGarmentSchema = createInsertSchema(runColorChangeGarments)
  .omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true })
  .extend({
    colorChangeId: z.uuid().nullable().optional(),
    quantity: z.number().int().positive(),
  });
export type InsertRunColorChangeGarment = z.infer<typeof insertRunColorChangeGarmentSchema>;
export type RunColorChangeGarment = typeof runColorChangeGarments.$inferSelect;

export const ZOHO_CHANGE_REVIEW_STATUSES = [
  "pending",
  "partially_applied",
  "applied",
  "superseded",
  "closed_without_apply",
] as const;

export const ZOHO_CHANGE_REVIEW_HUNK_STATUSES = [
  "pending",
  "applied",
  "blocked",
  "superseded",
  "closed_without_apply",
] as const;

export const ZOHO_CHANGE_REVIEW_HUNK_ENTITY_TYPES = [
  "work_order",
  "product",
  "run",
  "derived_total",
  "other",
] as const;

export type ZohoChangeReviewSnapshot = Record<string, unknown>;
export type ZohoSplitReviewHold = {
  sourceProductId: string;
  canonicalFingerprint: string;
  reason: "quantity" | "deletion" | "identity";
  baseUpstreamQuantity: number | null;
  incomingQuantity: number | null;
};

export type ZohoSplitReviewMetadata = {
  formatVersion: 1;
  sourceLineageId: string;
  lineageVersionAtCapture: number;
  // Existing proposedSnapshot remains the projected result for source A.
  upstreamSnapshot: ZohoChangeReviewSnapshot;
  holds: ZohoSplitReviewHold[];
};

export type ZohoChangeReviewSourceMetadata = Record<string, unknown> & {
  splitReview?: ZohoSplitReviewMetadata;
};

export const zohoChangeReviews = pgTable("zoho_change_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"),
  sourceEntryPoint: text("source_entrypoint").notNull(),
  zohoRecordId: text("zoho_record_id").notNull(),
  latestPayloadHash: text("latest_payload_hash").notNull(),
  latestDiffHash: text("latest_diff_hash").notNull(),
  latestZohoModifiedTime: timestamp("latest_zoho_modified_time"),
  parserVersion: text("parser_version"),
  sourceMetadata: jsonb("source_metadata").notNull().default(sql`'{}'::jsonb`).$type<ZohoChangeReviewSourceMetadata>(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_zoho_change_reviews_wo_id").on(table.workOrderId),
  index("idx_zoho_change_reviews_status").on(table.status).where(sql`deleted_at IS NULL`),
  index("idx_zoho_change_reviews_zoho_record_id").on(table.zohoRecordId),
  index("idx_zoho_change_reviews_latest_hashes").on(table.workOrderId, table.latestPayloadHash, table.latestDiffHash),
  uniqueIndex("uq_zoho_change_reviews_active_work_order")
    .on(table.workOrderId)
    .where(sql`deleted_at IS NULL AND status IN ('pending', 'partially_applied')`),
  check("zoho_change_reviews_status_check", sql`${table.status} IN ('pending', 'partially_applied', 'applied', 'superseded', 'closed_without_apply')`),
]);

export const zohoChangeReviewRevisions = pgTable("zoho_change_review_revisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  reviewId: varchar("review_id").notNull().references(() => zohoChangeReviews.id, { onDelete: "cascade" }),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  revisionNumber: integer("revision_number").notNull(),
  sourceEntryPoint: text("source_entrypoint").notNull(),
  zohoRecordId: text("zoho_record_id").notNull(),
  payloadHash: text("payload_hash").notNull(),
  diffHash: text("diff_hash").notNull(),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  parserVersion: text("parser_version"),
  baseSnapshot: jsonb("base_snapshot").notNull().$type<ZohoChangeReviewSnapshot>(),
  proposedSnapshot: jsonb("proposed_snapshot").notNull().$type<ZohoChangeReviewSnapshot>(),
  sourceMetadata: jsonb("source_metadata").notNull().default(sql`'{}'::jsonb`).$type<ZohoChangeReviewSourceMetadata>(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_zoho_change_review_revisions_review_id").on(table.reviewId),
  index("idx_zoho_change_review_revisions_wo_id").on(table.workOrderId),
  index("idx_zoho_change_review_revisions_hashes").on(table.workOrderId, table.payloadHash, table.diffHash),
  uniqueIndex("uq_zoho_change_review_revisions_number").on(table.reviewId, table.revisionNumber),
  uniqueIndex("uq_zoho_change_review_revisions_source_hash")
    .on(table.workOrderId, table.sourceEntryPoint, table.payloadHash, table.diffHash),
]);

export const zohoChangeReviewHunks = pgTable("zoho_change_review_hunks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  reviewId: varchar("review_id").notNull().references(() => zohoChangeReviews.id, { onDelete: "cascade" }),
  revisionId: varchar("revision_id").notNull().references(() => zohoChangeReviewRevisions.id, { onDelete: "cascade" }),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  entityType: text("entity_type").notNull(),
  matchedEntityId: varchar("matched_entity_id"),
  canonicalFingerprint: text("canonical_fingerprint"),
  fieldPath: text("field_path").notNull(),
  changeType: text("change_type").notNull(),
  baseValue: jsonb("base_value").$type<unknown | null>(),
  proposedValue: jsonb("proposed_value").$type<unknown | null>(),
  status: text("status").notNull().default("pending"),
  blockReason: text("block_reason"),
  dependencyGroup: text("dependency_group"),
  sortOrder: integer("sort_order").notNull().default(0),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_zoho_change_review_hunks_review_id").on(table.reviewId),
  index("idx_zoho_change_review_hunks_revision_id").on(table.revisionId),
  index("idx_zoho_change_review_hunks_wo_id").on(table.workOrderId),
  index("idx_zoho_change_review_hunks_status").on(table.status).where(sql`deleted_at IS NULL`),
  index("idx_zoho_change_review_hunks_dependency_group").on(table.dependencyGroup),
  check("zoho_change_review_hunks_status_check", sql`${table.status} IN ('pending', 'applied', 'blocked', 'superseded', 'closed_without_apply')`),
  check("zoho_change_review_hunks_entity_type_check", sql`${table.entityType} IN ('work_order', 'product', 'run', 'derived_total', 'other')`),
]);

export const insertZohoChangeReviewSchema = createInsertSchema(zohoChangeReviews).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertZohoChangeReview = z.infer<typeof insertZohoChangeReviewSchema>;
export type ZohoChangeReview = typeof zohoChangeReviews.$inferSelect;

export const insertZohoChangeReviewRevisionSchema = createInsertSchema(zohoChangeReviewRevisions).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertZohoChangeReviewRevision = z.infer<typeof insertZohoChangeReviewRevisionSchema>;
export type ZohoChangeReviewRevision = typeof zohoChangeReviewRevisions.$inferSelect;

export const insertZohoChangeReviewHunkSchema = createInsertSchema(zohoChangeReviewHunks).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertZohoChangeReviewHunk = z.infer<typeof insertZohoChangeReviewHunkSchema>;
export type ZohoChangeReviewHunk = typeof zohoChangeReviewHunks.$inferSelect;

export const missingProductReports = pgTable("missing_product_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  submittedByUserId: varchar("submitted_by_user_id").references(() => users.id, { onDelete: "set null" }),
  submittedByName: text("submitted_by_name").notNull(),
  reason: text("reason").notNull(),
  note: text("note"),
  status: text("status").notNull().default("reported"),
  replacementCallbackTokenHash: text("replacement_callback_token_hash"),
  replacementCallbackExpiresAt: timestamp("replacement_callback_expires_at"),
  replacementCallbackUsedAt: timestamp("replacement_callback_used_at"),
  replacementOrderedAt: timestamp("replacement_ordered_at"),
  replacementOrderedBy: text("replacement_ordered_by"),
  notificationEmailSent: boolean("notification_email_sent").notNull().default(false),
  notificationEmailWarning: text("notification_email_warning"),
  notificationEmailSentAt: timestamp("notification_email_sent_at"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_missing_product_reports_wo_id").on(table.workOrderId),
  index("idx_missing_product_reports_status").on(table.status),
  index("idx_missing_product_reports_submitted_by").on(table.submittedByUserId),
  index("idx_missing_product_reports_callback_hash").on(table.replacementCallbackTokenHash).where(sql`deleted_at IS NULL`),
  index("idx_missing_product_reports_active_wo").on(table.workOrderId).where(sql`deleted_at IS NULL`),
]);

export const missingProductReportItems = pgTable("missing_product_report_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  reportId: varchar("report_id").notNull().references(() => missingProductReports.id, { onDelete: "cascade" }),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  productId: varchar("product_id").references(() => workOrderProducts.id, { onDelete: "set null" }),
  productName: text("product_name"),
  productDisplayName: text("product_display_name"),
  productVendor: text("product_vendor"),
  vendorItemNumber: text("vendor_item_number"),
  color: text("color"),
  size: text("size"),
  reportedQuantity: integer("reported_quantity").notNull(),
  adjustedQuantity: integer("adjusted_quantity").notNull().default(0),
  quantityReceivedBefore: integer("quantity_received_before"),
  quantityReceivedAfter: integer("quantity_received_after"),
  productVersionAtSubmit: integer("product_version_at_submit"),
  status: text("status").notNull().default("adjusted"),
  skippedReason: text("skipped_reason"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_missing_product_report_items_report_id").on(table.reportId),
  index("idx_missing_product_report_items_wo_id").on(table.workOrderId),
  index("idx_missing_product_report_items_product_id").on(table.productId),
  index("idx_missing_product_report_items_status").on(table.status),
  index("idx_missing_product_report_items_active_report").on(table.reportId).where(sql`deleted_at IS NULL`),
]);

export const insertMissingProductReportSchema = createInsertSchema(missingProductReports).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertMissingProductReport = z.infer<typeof insertMissingProductReportSchema>;
export type MissingProductReport = typeof missingProductReports.$inferSelect;

export const insertMissingProductReportItemSchema = createInsertSchema(missingProductReportItems).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertMissingProductReportItem = z.infer<typeof insertMissingProductReportItemSchema>;
export type MissingProductReportItem = typeof missingProductReportItems.$inferSelect;

export const duffRecords = pgTable("duff_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  productId: varchar("product_id").notNull().references(() => workOrderProducts.id, { onDelete: "cascade" }),
  quantity: integer("quantity").notNull(),
  unitCost: numeric("unit_cost", { precision: 10, scale: 2 }),
  department: text("department"),
  machineId: varchar("machine_id").references(() => machines.id, { onDelete: "set null" }),
  machineName: text("machine_name"),
  issueType: text("issue_type"),
  teamMemberId: varchar("team_member_id").references(() => teamMembers.id, { onDelete: "set null" }),
  teamMemberName: text("team_member_name"),
  submittedBy: text("submitted_by").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_duff_records_wo_id").on(table.workOrderId),
  index("idx_duff_records_created_at").on(table.createdAt),
  index("idx_duff_records_department").on(table.department),
]);

export const insertDuffRecordSchema = createInsertSchema(duffRecords).omit({ id: true, createdAt: true });
export type InsertDuffRecord = z.infer<typeof insertDuffRecordSchema>;
export type DuffRecord = typeof duffRecords.$inferSelect;

export const QC_DEPARTMENTS = [
  "Screen Printing",
  "Embroidery",
  "Heatpress Transfer",
  "Design",
  "Production Art",
] as const;

export const QC_ISSUE_TYPES_BY_DEPARTMENT = {
  "Screen Printing": [
    "SCREEN CONTAMINATION", "SCREEN PINHOLE", "SCREEN STENCIL FAILURE",
    "PRINT DRY SCREEN", "PRINT INK ISSUE", "PRINT SMEAR", "PRINT BLEED",
    "PRINT LOW OPACITY", "PRINT FIBRILLATION", "PRINT CURE FAILURE",
    "REG OFF REGISTER", "REG PLACEMENT",
  ],
  Embroidery: [
    "STITCH BREAK", "STITCH QUALITY", "STITCH TENSION",
    "MACHINE THREAD NEST", "MACHINE NEEDLE ISSUE", "PLACEMENT OFF", "HOOP ISSUE",
  ],
  "Heatpress Transfer": [
    "APPLICATION SETTINGS", "APPLICATION MARKING", "ADHESION FAILURE",
    "ADHESION DURABILITY", "PLACEMENT OFF", "MATERIAL ISSUE",
  ],
  Design: [
    "Adjust blow-up for no underlay",
    "Thicken thin lines/type for printability",
    "Add bleed",
    "Match art to template",
    "Convert all colors to Pantone spot colors",
    "Replace Match Color with Pantone",
    "Add / subtract / correct color callouts",
    "Resolve mockup vs location discrepancies",
    "Correct product and HT type callouts",
    "Add Raised color callouts",
    "Fix obvious art issues",
    "Correct misspelled text",
    "Remove unintended / extraneous objects",
    "Undo merge effects to allow edits",
    "Replace low-resolution images",
    "Convert RGB → CMYK",
    "Adjust bitmap quality",
    "Template restructuring",
    "Major cleanup of complex or messy files",
  ],
  "Production Art": [
    "SEPARATION MISTAKE", "WRONG ART",
  ],
} as const satisfies Record<(typeof QC_DEPARTMENTS)[number], readonly string[]>;

export const QC_ISSUE_TYPES = Array.from(new Set(Object.values(QC_ISSUE_TYPES_BY_DEPARTMENT).flat()));

export const DUFF_DEPARTMENTS = [
  "Heatpress",
  "Embroidery",
  "Screenprinting",
  "Production Art",
  "Product Handling",
  "External",
] as const;

export const DUFF_ISSUE_TYPES_BY_DEPARTMENT = {
  Heatpress: [
    "APPLICATION SETTINGS",
    "APPLICATION MARKING",
    "ADHESION FAILURE",
    "ADHESION DURABILITY",
    "PLACEMENT OFF",
    "MATERIAL ISSUE",
  ],
  Embroidery: [
    "STITCH BREAK",
    "STITCH QUALITY",
    "STITCH TENSION",
    "MACHINE THREAD NEST",
    "MACHINE NEEDLE ISSUE",
    "PLACEMENT OFF",
    "HOOP ISSUE",
  ],
  Screenprinting: [
    "SCREEN CONTAMINATION",
    "SCREEN PINHOLE",
    "SCREEN STENCIL FAILURE",
    "PRINT DRY SCREEN",
    "PRINT INK ISSUE",
    "PRINT SMEAR",
    "PRINT BLEED",
    "PRINT LOW OPACITY",
    "PRINT FIBRILLATION",
    "PRINT CURE FAILURE",
    "REG OFF REGISTER",
    "REG PLACEMENT",
  ],
  "Production Art": [
    "SEPARATION MISTAKE",
    "WRONG ART",
  ],
  "Product Handling": [
    "MISSED DIRTY/DAMAGED PRODUCT",
    "MISSED WRONG PRODUCT SENT BY VENDOR",
  ],
  External: [
    "Design Error",
    "Account Manager Error",
    "Order Management Error",
  ],
} as const satisfies Record<(typeof DUFF_DEPARTMENTS)[number], readonly string[]>;

export const DUFF_ISSUE_TYPES = Array.from(new Set(Object.values(DUFF_ISSUE_TYPES_BY_DEPARTMENT).flat()));

export const qcSaves = pgTable("qc_saves", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").references(() => workOrders.id, { onDelete: "set null" }),
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "set null" }),
  responsibleSpecialty: text("responsible_specialty"),
  responsibleTeamMemberId: varchar("responsible_team_member_id").references(() => teamMembers.id, { onDelete: "set null" }),
  responsiblePartyName: text("responsible_party_name"),
  date: timestamp("date").notNull(),
  jobPoNumber: text("job_po_number").notNull(),
  department: text("department").notNull(),
  machineId: varchar("machine_id").references(() => machines.id, { onDelete: "set null" }),
  machineName: text("machine_name"),
  issueType: text("issue_type").notNull(),
  orderQty: integer("order_qty").notNull().default(0),
  impressions: integer("impressions").notNull().default(0),
  qtySaved: integer("qty_saved").notNull().default(0),
  teamMemberId: varchar("team_member_id").references(() => teamMembers.id, { onDelete: "set null" }),
  teamMemberName: text("team_member_name"),
  notes: text("notes"),
  submittedBy: text("submitted_by").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_qc_saves_date").on(table.date),
  index("idx_qc_saves_work_order_id").on(table.workOrderId).where(sql`work_order_id IS NOT NULL`),
  index("idx_qc_saves_job_po_number").on(table.jobPoNumber),
  index("idx_qc_saves_run_id").on(table.runId),
  index("idx_qc_saves_responsible_member").on(table.responsibleTeamMemberId),
  check("qc_saves_one_responsible_party", sql`${table.responsibleSpecialty} IS NULL OR ${table.responsibleTeamMemberId} IS NULL`),
]);

export const insertQcSaveSchema = createInsertSchema(qcSaves).omit({ id: true, createdAt: true });
export type InsertQcSave = z.infer<typeof insertQcSaveSchema>;
export type QcSave = typeof qcSaves.$inferSelect;

export const scheduleEvents = pgTable("schedule_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "cascade" }),
  machineId: varchar("machine_id").notNull().references(() => machines.id, { onDelete: "restrict" }),
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time").notNull(),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_schedule_events_machine_id").on(table.machineId),
  index("idx_schedule_events_wo_id").on(table.workOrderId),
  index("idx_schedule_events_run_id").on(table.runId),
  index("idx_schedule_events_time_range").on(table.startTime, table.endTime),
  index("idx_schedule_events_active_machine").on(table.machineId, table.startTime, table.endTime).where(sql`deleted_at IS NULL`),
]);

export const insertScheduleEventSchema = createInsertSchema(scheduleEvents).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertScheduleEvent = z.infer<typeof insertScheduleEventSchema>;
export type ScheduleEvent = typeof scheduleEvents.$inferSelect;

export const scheduleEventProductAllocations = pgTable("schedule_event_product_allocations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scheduleEventId: varchar("schedule_event_id").notNull().references(() => scheduleEvents.id, { onDelete: "cascade" }),
  workOrderProductId: varchar("work_order_product_id").notNull().references(() => workOrderProducts.id, { onDelete: "cascade" }),
  quantity: integer("quantity").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_sched_alloc_event").on(table.scheduleEventId),
  index("idx_sched_alloc_product").on(table.workOrderProductId),
  index("idx_sched_alloc_active_event").on(table.scheduleEventId).where(sql`deleted_at IS NULL`),
  index("idx_sched_alloc_active_product").on(table.workOrderProductId).where(sql`deleted_at IS NULL`),
  uniqueIndex("uq_sched_alloc_active_event_product").on(table.scheduleEventId, table.workOrderProductId).where(sql`deleted_at IS NULL`),
  check("schedule_event_product_allocations_quantity_check", sql`${table.quantity} >= 0`),
]);

export const insertScheduleEventProductAllocationSchema = createInsertSchema(scheduleEventProductAllocations).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertScheduleEventProductAllocation = z.infer<typeof insertScheduleEventProductAllocationSchema>;
export type ScheduleEventProductAllocation = typeof scheduleEventProductAllocations.$inferSelect;

export const fileAttachments = pgTable("file_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "set null" }),
  // null = the run's "Original" version (HUB-391). Cascades when a color version is hard-deleted.
  colorChangeId: varchar("color_change_id").references(() => runColorChanges.id, { onDelete: "cascade" }),
  fileName: text("file_name").notNull(),
  fileType: text("file_type").notNull(),
  filePath: text("file_path").notNull(),
  category: text("category").notNull().default("mockup"),
  dropboxPath: text("dropbox_path"),
  dropboxPreviewUrl: text("dropbox_preview_url"),
  dropboxSharedLink: text("dropbox_shared_link"),
  uploadedAt: timestamp("uploaded_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_file_attachments_wo_id").on(table.workOrderId),
  index("idx_file_attachments_run_id").on(table.runId),
  index("idx_file_attachments_category").on(table.category),
  index("idx_file_attachments_color_change_id").on(table.colorChangeId).where(sql`color_change_id IS NOT NULL`),
]);

export const insertFileAttachmentSchema = createInsertSchema(fileAttachments)
  .omit({ id: true, uploadedAt: true, updatedAt: true, deletedAt: true })
  .extend({
    colorChangeId: z.uuid().nullable().optional(),
  });
export type InsertFileAttachment = z.infer<typeof insertFileAttachmentSchema>;
export type FileAttachment = typeof fileAttachments.$inferSelect;

export const processMilestones = pgTable("process_milestones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "cascade" }),
  runLocation: text("run_location"),
  stepKey: text("step_key").notNull(),
  stepLabel: text("step_label").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  completed: boolean("completed").notNull().default(false),
  completedAt: timestamp("completed_at"),
  completedBy: text("completed_by"),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_process_milestones_wo_id").on(table.workOrderId),
  index("idx_process_milestones_run_id").on(table.runId),
  index("idx_process_milestones_completed").on(table.completed),
  index("idx_process_milestones_completed_at").on(table.completed, table.completedAt),
  uniqueIndex("uq_process_milestones_active_identity")
    .on(table.workOrderId, table.stepKey, sql`coalesce(${table.runId}, '')`)
    .where(sql`deleted_at IS NULL`),
]);

export const insertProcessMilestoneSchema = createInsertSchema(processMilestones).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertProcessMilestone = z.infer<typeof insertProcessMilestoneSchema>;
export type ProcessMilestone = typeof processMilestones.$inferSelect;

export const milestoneComments = pgTable("milestone_comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  milestoneId: varchar("milestone_id").notNull().references(() => processMilestones.id, { onDelete: "cascade" }),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  author: text("author").notNull(),
  body: text("body").notNull(),
  mentions: text("mentions").array().default([]),
  imageUrl: text("image_url"),
  imageDropboxLink: text("image_dropbox_link"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_milestone_comments_milestone_id").on(table.milestoneId),
  index("idx_milestone_comments_wo_id").on(table.workOrderId),
  index("idx_milestone_comments_milestone_created").on(table.milestoneId, table.createdAt),
]);

export const insertMilestoneCommentSchema = createInsertSchema(milestoneComments).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertMilestoneComment = z.infer<typeof insertMilestoneCommentSchema>;
export type MilestoneComment = typeof milestoneComments.$inferSelect;

export const workOrderNotes = pgTable("work_order_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  authorUserId: varchar("author_user_id").references(() => users.id, { onDelete: "set null" }),
  authorName: text("author_name").notNull(),
  body: text("body").notNull(),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_work_order_notes_wo_id").on(table.workOrderId),
  index("idx_work_order_notes_author_user_id").on(table.authorUserId),
  index("idx_work_order_notes_active_wo").on(table.workOrderId).where(sql`deleted_at IS NULL`),
]);

export const insertWorkOrderNoteSchema = createInsertSchema(workOrderNotes).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertWorkOrderNote = z.infer<typeof insertWorkOrderNoteSchema>;
export type WorkOrderNote = typeof workOrderNotes.$inferSelect;

export const roleInstructions = pgTable("role_instructions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "set null" }),
  body: text("body").notNull(),
  authorUserId: varchar("author_user_id").references(() => users.id, { onDelete: "set null" }),
  authorName: text("author_name").notNull(),
  targetSpecialties: text("target_specialties").array().notNull().default(sql`'{}'::text[]`),
  targetUserIds: text("target_user_ids").array().notNull().default(sql`'{}'::text[]`),
  targetStepKeys: text("target_step_keys").array().notNull().default(sql`'{}'::text[]`),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_role_instructions_wo_id").on(table.workOrderId),
  index("idx_role_instructions_run_id").on(table.runId),
  index("idx_role_instructions_author_user_id").on(table.authorUserId),
  index("idx_role_instructions_active_wo").on(table.workOrderId).where(sql`deleted_at IS NULL`),
]);

export const insertRoleInstructionSchema = createInsertSchema(roleInstructions).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertRoleInstruction = z.infer<typeof insertRoleInstructionSchema>;
export type RoleInstruction = typeof roleInstructions.$inferSelect;

export const teamMembers = pgTable("team_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  displayName: text("display_name").notNull().unique(),
  firstName: text("first_name").notNull().default(""),
  lastName: text("last_name").notNull().default(""),
  email: text("email"),
  role: text("role").notNull().default("team_member"),
  specialty: text("specialty"),
  specialties: text("specialties").array().default(sql`'{}'::text[]`),
  managedSpecialties: text("managed_specialties").array().default(sql`'{}'::text[]`),
  profilePicPath: text("profile_pic_path"),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  dashboardPreferences: jsonb("dashboard_preferences"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  check("team_members_role_check", sql`${table.role} IN ('admin', 'manager', 'team_member')`),
  index("idx_team_members_user_id").on(table.userId),
  uniqueIndex("idx_team_members_email_unique").on(table.email),
]);

export const insertTeamMemberSchema = createInsertSchema(teamMembers).omit({ id: true, version: true, userId: true, createdAt: true, updatedAt: true, deletedAt: true, dashboardPreferences: true });
export type InsertTeamMember = z.infer<typeof insertTeamMemberSchema>;
export type TeamMember = typeof teamMembers.$inferSelect;

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recipientUserId: varchar("recipient_user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  type: text("type").notNull(),
  message: text("message").notNull(),
  workOrderId: varchar("work_order_id").references(() => workOrders.id, { onDelete: "cascade" }),
  milestoneId: varchar("milestone_id").references(() => processMilestones.id, { onDelete: "cascade" }),
  commentId: varchar("comment_id").references(() => milestoneComments.id, { onDelete: "cascade" }),
  qcSaveId: varchar("qc_save_id").references(() => qcSaves.id, { onDelete: "cascade" }),
  read: boolean("read").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_notifications_recipient").on(table.recipientUserId),
  index("idx_notifications_read").on(table.read),
  index("idx_notifications_recipient_read").on(table.recipientUserId, table.read),
  index("idx_notifications_created_at").on(table.createdAt),
  index("idx_notifications_deleted_at").on(table.deletedAt),
  index("idx_notifications_qc_save_id").on(table.qcSaveId),
  uniqueIndex("idx_notifications_qc_save_recipient").on(table.recipientUserId, table.qcSaveId)
    .where(sql`deleted_at IS NULL AND qc_save_id IS NOT NULL`),
  index("idx_notifications_recipient_recent").on(table.recipientUserId, table.createdAt),
  uniqueIndex("idx_notifications_event_unique").on(
    table.recipientUserId,
    table.type,
    table.message,
    sql`coalesce(${table.workOrderId}, '')`,
    sql`coalesce(${table.milestoneId}, '')`,
    sql`coalesce(${table.commentId}, '')`,
  ).where(sql`deleted_at IS NULL AND qc_save_id IS NULL`),
]);

export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true, createdAt: true, deletedAt: true });
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// Idempotency keys for create endpoints (PRO-161). Infrastructure table —
// intentionally NOT versioned and NOT soft-deleted. `requestHash` folds in the
// effective user id, method, route, route params, and canonical payload so the
// same client key can never replay across users or a different request shape.
// `keyHash` stores sha256 of the raw client key (raw key is never persisted).
export const idempotencyKeys = pgTable("idempotency_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  keyHash: text("key_hash").notNull(),
  method: text("method").notNull(),
  route: text("route").notNull(),
  routeParamsHash: text("route_params_hash").notNull(),
  requestHash: text("request_hash").notNull(),
  status: text("status").notNull().default("in_progress"),
  responseStatus: integer("response_status"),
  responseBody: jsonb("response_body"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  check("idempotency_keys_status_check", sql`${table.status} IN ('in_progress', 'completed')`),
  uniqueIndex("idx_idempotency_keys_user_key_unique").on(table.userId, table.keyHash),
  index("idx_idempotency_keys_expires_at").on(table.expiresAt),
]);

export const insertIdempotencyKeySchema = createInsertSchema(idempotencyKeys).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertIdempotencyKey = z.infer<typeof insertIdempotencyKeySchema>;
export type IdempotencyKey = typeof idempotencyKeys.$inferSelect;

export const reprintAlerts = pgTable("reprint_alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  originalWorkOrderId: varchar("original_work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  reprintWorkOrderId: varchar("reprint_work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  originalOrderNumber: text("original_order_number").notNull(),
  message: text("message").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_reprint_alerts_created_at").on(table.createdAt),
]);

export const reprintAlertAcks = pgTable("reprint_alert_acks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  alertId: varchar("alert_id").notNull().references(() => reprintAlerts.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  acknowledgedAt: timestamp("acknowledged_at").notNull().default(sql`now()`),
}, (table) => [
  uniqueIndex("idx_reprint_alert_acks_unique").on(table.alertId, table.userId),
]);

export type ReprintAlert = typeof reprintAlerts.$inferSelect;
export type ReprintAlertAck = typeof reprintAlertAcks.$inferSelect;

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  username: text("username"),
  workOrderId: varchar("work_order_id").references(() => workOrders.id, { onDelete: "set null" }),
  // Optional run discriminator. Populated by `run_update` audit writes so
  // the unified note feed can attribute run-level edits to a specific run
  // when multiple runs on the same WO have edited the same column. Stays
  // null on WO-level (`update`) audits and on historical rows.
  runId: varchar("run_id").references(() => workOrderRuns.id, { onDelete: "set null" }),
  action: text("action").notNull(),
  field: text("field"),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  details: text("details"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_audit_logs_wo_id").on(table.workOrderId),
  index("idx_audit_logs_created_at").on(table.createdAt),
  index("idx_audit_logs_action").on(table.action),
  index("idx_audit_logs_run_id").on(table.runId).where(sql`run_id IS NOT NULL`),
  index("idx_audit_logs_field_new_value").on(table.field, table.newValue),
]);

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({ id: true, createdAt: true });
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

export const screenSpecs = pgTable("screen_specs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => workOrderRuns.id, { onDelete: "cascade" }),
  location: text("location").notNull(),
  colorName: text("color_name").notNull(),
  pmsColor: text("pms_color"),
  meshCount: text("mesh_count").notNull(),
  burnMode: text("burn_mode").default("new"),
  status: text("status").notNull().default("spec"),
  burnedAt: timestamp("burned_at"),
  burnedBy: text("burned_by"),
  sharedAt: timestamp("shared_at"),
  sharedBy: text("shared_by"),
  sharedWithRunId: varchar("shared_with_run_id").references(() => workOrderRuns.id, { onDelete: "set null" }),
  screenNotes: text("screen_notes"),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_screen_specs_wo_id").on(table.workOrderId),
  index("idx_screen_specs_run_id").on(table.runId),
  index("idx_screen_specs_wo_run").on(table.workOrderId, table.runId),
  index("idx_screen_specs_active_wo_run").on(table.workOrderId, table.runId).where(sql`deleted_at IS NULL`),
  check("screen_specs_status_check", sql`${table.status} IN ('spec', 'burned', 'reclaimed', 'shared')`),
]);

export const insertScreenSpecSchema = createInsertSchema(screenSpecs).omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertScreenSpec = z.infer<typeof insertScreenSpecSchema>;
export type ScreenSpec = typeof screenSpecs.$inferSelect;

export const inkFormulas = pgTable("ink_formulas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => workOrderRuns.id, { onDelete: "cascade" }),
  // null = the run's "Original" version (HUB-391). FK is the composite (run_id, color_change_id)
  // declared in the table builder below, so an ink's version must belong to its own run.
  colorChangeId: varchar("color_change_id"),
  location: text("location").notNull(),
  colorName: text("color_name").notNull(),
  pmsColor: text("pms_color"),
  batchSize: integer("batch_size").default(1000),
  status: text("status").notNull().default("pending"),
  mixedAt: timestamp("mixed_at"),
  mixedBy: text("mixed_by"),
  // HUB-574: recipe-book snapshot + the grams actually used, written only by mark-mixed.
  // NULL for inks mixed before HUB-574 or with no recipe on file ("not recorded").
  mixRecord: jsonb("mix_record").$type<InkMixRecord>(),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_ink_formulas_wo_id").on(table.workOrderId),
  index("idx_ink_formulas_run_id").on(table.runId),
  index("idx_ink_formulas_wo_run").on(table.workOrderId, table.runId),
  index("idx_ink_formulas_active_wo_run").on(table.workOrderId, table.runId).where(sql`deleted_at IS NULL`),
  index("idx_ink_formulas_run_color_change").on(table.runId, table.colorChangeId).where(sql`deleted_at IS NULL`),
  // Serves the composite FK cascade; partial-on-NOT-NULL keeps the mostly-Original (null) rows out.
  index("idx_ink_formulas_color_change_id").on(table.colorChangeId).where(sql`color_change_id IS NOT NULL`),
  // Composite FK: an ink's color version must belong to the SAME run (run-membership integrity),
  // not merely exist. MATCH SIMPLE leaves Original rows (color_change_id IS NULL) unconstrained.
  foreignKey({
    columns: [table.runId, table.colorChangeId],
    foreignColumns: [runColorChanges.runId, runColorChanges.id],
    name: "ink_formulas_run_color_change_fk",
  }).onDelete("cascade"),
]);

export const insertInkFormulaSchema = createInsertSchema(inkFormulas)
  // mixRecord is server-built inside mark-mixed only; create/patch bodies must never set it.
  .omit({ id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true, mixRecord: true })
  .extend({
    colorChangeId: z.uuid().nullable().optional(),
  });
export type InsertInkFormula = z.infer<typeof insertInkFormulaSchema>;
export type InkFormula = typeof inkFormulas.$inferSelect;

// Cached ink mixing recipes scraped from Avient IMS
export const inkRecipes = pgTable("ink_recipes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  pmsColor: text("pms_color").notNull(),
  mixingSystem: text("mixing_system").notNull(),
  ingredients: jsonb("ingredients").notNull().$type<{ name: string; code?: string; percentage: number }[]>(),
  sourceUrl: text("source_url"),
  scrapedAt: timestamp("scraped_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  uniqueIndex("idx_ink_recipes_pms_system").on(table.pmsColor, table.mixingSystem),
  index("idx_ink_recipes_pms").on(table.pmsColor),
]);

export const insertInkRecipeSchema = createInsertSchema(inkRecipes).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertInkRecipe = z.infer<typeof insertInkRecipeSchema>;
export type InkRecipe = typeof inkRecipes.$inferSelect;

export const SCREENPRINT_STEPS = [
  { key: "prt_files_sent", label: "Separation File(s) Sent", order: 1, track: "main", requires: [] as string[] },
  { key: "screens_burned", label: "Screen(s) Burned", order: 2, track: "main", requires: ["prt_files_sent"] },
  { key: "ink_mixed", label: "Ink(s) Mixed", order: 3, track: "concurrent", requires: [] as string[] },
  { key: "press_setup", label: "Press Set Up", order: 4, track: "main", requires: ["screens_burned", "ink_mixed"] },
  { key: "blank_product_received", label: "Blank Product Received", order: 5, track: "concurrent2", requires: [] as string[] },
  { key: "product_laid_out", label: "Product Laid Out", order: 6, track: "concurrent2", requires: ["blank_product_received"] },
  { key: "production_run", label: "Production Run", order: 7, track: "final", requires: ["press_setup", "product_laid_out"] },
  { key: "quality_check", label: "Quality Check", order: 8, track: "final", requires: ["production_run"] },
  { key: "packing", label: "Packed", order: 9, track: "final", requires: ["quality_check"] },
  { key: "complete", label: "Complete", order: 10, track: "final", requires: ["packing"] },
] as const;

export const EMBROIDERY_STEPS = [
  { key: "art_sent_digitizing", label: "Art Sent for Digitizing", order: 1, track: "main", requires: [] as string[] },
  { key: "dst_files_created", label: "DST File(s) Created", order: 2, track: "main", requires: ["art_sent_digitizing"] },
  { key: "sample_sewout", label: "Sample Sew-out", order: 3, track: "main", requires: ["dst_files_created"] },
  { key: "sewout_approved", label: "Sew-out Approved", order: 4, track: "main", requires: ["sample_sewout"] },
  { key: "blank_product_received", label: "Blank Product Received", order: 5, track: "concurrent", requires: [] as string[] },
  { key: "emb_product_laid_out", label: "Product Laid Out", order: 6, track: "concurrent", requires: ["blank_product_received"] },
  { key: "emb_production_run", label: "Production Run", order: 7, track: "final", requires: ["sewout_approved", "emb_product_laid_out"] },
  { key: "emb_quality_check", label: "Quality Check", order: 8, track: "final", requires: ["emb_production_run"] },
  { key: "emb_packing", label: "Packed", order: 9, track: "final", requires: ["emb_quality_check"] },
  { key: "emb_complete", label: "Complete", order: 10, track: "final", requires: ["emb_packing"] },
] as const;

export const HEATPRESS_STEPS = [
  { key: "hp_transfers_ordered", label: "Transfers Ordered Online", order: 1, track: "main", requires: [] as string[] },
  { key: "hp_transfers_received", label: "Transfers Received", order: 2, track: "main", requires: ["hp_transfers_ordered"] },
  { key: "hp_blank_product_received", label: "Blank Product Received", order: 3, track: "concurrent", requires: [] as string[] },
  { key: "hp_product_laid_out", label: "Product Laid Out", order: 4, track: "concurrent", requires: ["hp_blank_product_received"] },
  { key: "hp_production_run", label: "Production Run", order: 5, track: "final", requires: ["hp_transfers_received", "hp_product_laid_out"] },
  { key: "hp_quality_check", label: "Quality Check", order: 6, track: "final", requires: ["hp_production_run"] },
  { key: "hp_packing", label: "Packed", order: 7, track: "final", requires: ["hp_quality_check"] },
  { key: "hp_complete", label: "Complete", order: 8, track: "final", requires: ["hp_packing"] },
] as const;

export const POLYBAG_STEPS = [
  { key: "pb_finished_goods_laid_out", label: "Finished Goods Laid Out", order: 1, track: "concurrent", requires: [] as string[] },
  { key: "pb_sku_label_art_ready", label: "SKU Label Art Ready", order: 2, track: "concurrent2", requires: [] as string[] },
  { key: "pb_production_run", label: "Production Run", order: 3, track: "final", requires: ["pb_finished_goods_laid_out", "pb_sku_label_art_ready"] },
  { key: "pb_quality_check", label: "Quality Check", order: 4, track: "final", requires: ["pb_production_run"] },
  { key: "pb_packing", label: "Packing", order: 5, track: "final", requires: ["pb_quality_check"] },
  { key: "pb_complete", label: "Complete", order: 6, track: "final", requires: ["pb_packing"] },
] as const;

export type StepDef = { key: string; label: string; order: number; track: string; requires: readonly string[] };

export function getProductReceivingStepKey(decorationType: string | null | undefined): string {
  if (decorationType === "heat_press") return "hp_blank_product_received";
  if (decorationType === "poly_bagging") return "pb_finished_goods_laid_out";
  return "blank_product_received";
}

export function areAllProductsReceived(
  products: Array<{ quantity: number | null; quantityReceived: number | null }>,
): boolean {
  return products.length > 0 && products.every(
    (product) => (product.quantityReceived || 0) >= (product.quantity || 0),
  );
}

export const RUN_SPECIFIC_STEPS: Record<string, string[]> = {
  screenprint: ["prt_files_sent", "screens_burned", "ink_mixed", "press_setup", "production_run"],
  embroidery: ["art_sent_digitizing", "dst_files_created", "sample_sewout", "sewout_approved"],
  heat_press: ["hp_transfers_ordered", "hp_transfers_received"],
  poly_bagging: [],
};

export function isRunSpecificStep(decorationType: string, stepKey: string): boolean {
  return (RUN_SPECIFIC_STEPS[decorationType] || []).includes(stepKey);
}

type RunSpecificMilestoneLike = { stepKey: string; runId: string | null; completed: boolean };
type RunSpecificRunLike = { id: string; detailedDecoType?: string | null; location?: string | null };

export function isRunSpecificRequirementCompleted(
  reqKey: string,
  milestones: RunSpecificMilestoneLike[],
  runs: RunSpecificRunLike[],
  decorationType: string,
): boolean {
  const runMilestones = milestones.filter((m) => m.stepKey === reqKey && m.runId);
  if (runMilestones.length === 0) {
    const sharedMilestone = milestones.find((m) => m.stepKey === reqKey && !m.runId);
    return sharedMilestone?.completed === true;
  }

  const requiredRuns = runs.filter((run) => !isExcludedRun(run, decorationType));
  if (requiredRuns.length === 0) return runMilestones.every((m) => m.completed);

  return requiredRuns.every((run) =>
    runMilestones.some((m) => m.runId === run.id && m.completed),
  );
}

export function isFeeOrChargeRun(run: { detailedDecoType?: string | null; location?: string | null }): boolean {
  const desc = (run.detailedDecoType || "").toUpperCase();
  return desc.includes("FEE") || desc.includes("CHARGE");
}

export function isHeatPressNonApplicationRun(run: { detailedDecoType?: string | null }, decorationType: string): boolean {
  if (decorationType !== "heat_press") return false;
  const desc = (run.detailedDecoType || "").toUpperCase();
  return !desc.includes("APPLICATION");
}

export function isExcludedRun(run: { detailedDecoType?: string | null; location?: string | null }, decorationType: string): boolean {
  return isFeeOrChargeRun(run) || isHeatPressNonApplicationRun(run, decorationType);
}

export const DECORATION_TYPES = [
  "screenprint",
  "embroidery",
  "heat_press",
  "poly_bagging",
] as const;

export const WORK_ORDER_STATUSES = [
  "pending",
  "scheduled",
  "ready",
  "packing",
  "complete",
] as const;

export const PRIORITY_LEVELS = [
  "low",
  "normal",
  "high",
  "firm",
  "rush",
] as const;

export const MACHINE_TYPES = [
  "screenprint_press",
  "embroidery_machine",
  "heat_press",
  "poly_bagger",
] as const;

export const appSettings = pgTable("app_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  value: text("value").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
});

export const insertAppSettingSchema = createInsertSchema(appSettings).omit({ id: true });
export type InsertAppSetting = z.infer<typeof insertAppSettingSchema>;
export type AppSetting = typeof appSettings.$inferSelect;

export const screenInventory = pgTable("screen_inventory", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  mesh: text("mesh").notNull().unique(),
  raw: integer("raw").notNull().default(0),
  coated: doublePrecision("coated").notNull().default(0),
  version: integer("version").notNull().default(1),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  updatedBy: text("updated_by"),
});

export const insertScreenInventorySchema = createInsertSchema(screenInventory).omit({ id: true, version: true, updatedAt: true });
export type InsertScreenInventory = z.infer<typeof insertScreenInventorySchema>;
export type ScreenInventoryRow = typeof screenInventory.$inferSelect;

// ── Ink Inventory (shared across ink mixers) ─────────────────────────
export const inkInventory = pgTable("ink_inventory", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  stockLevel: doublePrecision("stock_level").notNull().default(0),
  unitCost: numeric("unit_cost", { precision: 10, scale: 4, mode: "number" }).notNull().default(0),
  type: text("type").notNull().default("pigment"),
  version: integer("version").notNull().default(1),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  updatedBy: text("updated_by"),
});

export const insertInkInventorySchema = createInsertSchema(inkInventory).omit({ id: true, version: true, updatedAt: true });
export type InsertInkInventory = z.infer<typeof insertInkInventorySchema>;
export type InkInventoryRow = typeof inkInventory.$inferSelect;

export const inkUsageLog = pgTable("ink_usage_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  inkInventoryId: varchar("ink_inventory_id").references(() => inkInventory.id, { onDelete: "cascade" }),
  itemName: text("item_name").notNull(),
  quantityGrams: doublePrecision("quantity_grams").notNull(),
  usedBy: text("used_by"),
  reason: text("reason"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
});

export const insertInkUsageLogSchema = createInsertSchema(inkUsageLog).omit({ id: true, createdAt: true });
export type InsertInkUsageLog = z.infer<typeof insertInkUsageLogSchema>;
export type InkUsageLogEntry = typeof inkUsageLog.$inferSelect;

export const DECO_TO_MACHINE_TYPE: Record<string, string[]> = {
  screenprint: ["screenprint_press"],
  embroidery: ["embroidery_machine"],
  heat_press: ["heat_press"],
  poly_bagging: ["poly_bagger"],
};

export const SPECIALTY_TO_DECO: Record<string, string[]> = {
  press_operator: ["screenprint"],
  screen_burner: ["screenprint"],
  ink_mixer: ["screenprint"],
  embroidery_operator: ["embroidery"],
  digitizer: ["embroidery"],
  heat_press_operator: ["heat_press"],
  hp_transfer_orderer: ["heat_press"],
  polybag_operator: ["poly_bagging"],
  product_handler: ["screenprint", "embroidery", "heat_press", "poly_bagging"],
  quality_control: ["screenprint", "embroidery", "heat_press", "poly_bagging"],
  art_department: ["screenprint", "embroidery", "heat_press", "poly_bagging"],
};

export const SPECIALTIES = [
  { value: "art_department", label: "Production Artist" },
  { value: "screen_burner", label: "Screen Burner" },
  { value: "ink_mixer", label: "Ink Mixer" },
  { value: "press_operator", label: "Press Operator" },
  { value: "embroidery_operator", label: "Embroidery Operator" },
  { value: "digitizer", label: "Digitizer" },
  { value: "heat_press_operator", label: "Heatpress Operator" },
  { value: "hp_transfer_orderer", label: "HP Transfer Orderer" },
  { value: "product_handler", label: "Product Handler" },
  { value: "quality_control", label: "Quality Controller" },
  { value: "polybag_operator", label: "Polybag Operator" },
  { value: "order_management", label: "Order Management" },
  { value: "account_manager", label: "Account Manager" },
] as const;

export const MILESTONE_TO_SPECIALTY: Record<string, string> = {
  prt_files_sent: "art_department",
  screens_burned: "screen_burner",
  ink_mixed: "ink_mixer",
  press_setup: "press_operator",
  blank_product_received: "product_handler",
  product_laid_out: "product_handler",
  production_run: "press_operator",
  quality_check: "quality_control",
  packing: "product_handler",
  complete: "product_handler",
  art_sent_digitizing: "art_department",
  dst_files_created: "digitizer",
  sample_sewout: "embroidery_operator",
  sewout_approved: "embroidery_operator",
  emb_product_laid_out: "product_handler",
  emb_production_run: "embroidery_operator",
  emb_quality_check: "quality_control",
  emb_packing: "product_handler",
  emb_complete: "product_handler",
  hp_transfers_ordered: "art_department",
  hp_transfers_received: "product_handler",
  hp_blank_product_received: "product_handler",
  hp_product_laid_out: "product_handler",
  hp_production_run: "heat_press_operator",
  hp_quality_check: "quality_control",
  hp_packing: "product_handler",
  hp_complete: "product_handler",
  pb_finished_goods_laid_out: "product_handler",
  pb_sku_label_art_ready: "art_department",
  pb_production_run: "polybag_operator",
  pb_quality_check: "quality_control",
  pb_packing: "product_handler",
  pb_complete: "product_handler",
};

export const syncFailures = pgTable("sync_failures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  source: text("source").notNull(), // "zoho", "avient"
  recordId: text("record_id"),
  errorMessage: text("error_message").notNull(),
  payload: jsonb("payload"),
  attempts: integer("attempts").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by", { length: 255 }),
});

export type SyncFailure = typeof syncFailures.$inferSelect;
export type InsertSyncFailure = typeof syncFailures.$inferInsert;
export const insertSyncFailureSchema = createInsertSchema(syncFailures).omit({ id: true, createdAt: true });

export const MILESTONE_SPECIALTIES: Record<string, string[]> = {
  prt_files_sent: ["art_department"],
  screens_burned: ["screen_burner"],
  ink_mixed: ["ink_mixer"],
  press_setup: ["screen_burner", "ink_mixer", "press_operator"],
  blank_product_received: ["product_handler"],
  product_laid_out: ["product_handler"],
  production_run: ["press_operator"],
  quality_check: ["product_handler", "quality_control"],
  packing: ["product_handler", "quality_control"],
  complete: ["product_handler", "quality_control"],
  art_sent_digitizing: ["art_department"],
  dst_files_created: ["digitizer"],
  sample_sewout: ["embroidery_operator"],
  sewout_approved: ["embroidery_operator"],
  emb_product_laid_out: ["product_handler"],
  emb_production_run: ["embroidery_operator"],
  emb_quality_check: ["product_handler", "quality_control"],
  emb_packing: ["product_handler", "quality_control"],
  emb_complete: ["product_handler", "quality_control"],
  hp_transfers_ordered: ["art_department", "product_handler", "hp_transfer_orderer"],
  hp_transfers_received: ["art_department", "product_handler", "hp_transfer_orderer"],
  hp_blank_product_received: ["product_handler"],
  hp_product_laid_out: ["product_handler"],
  hp_production_run: ["heat_press_operator"],
  hp_quality_check: ["product_handler", "quality_control"],
  hp_packing: ["product_handler", "quality_control"],
  hp_complete: ["product_handler", "quality_control"],
  pb_finished_goods_laid_out: ["product_handler"],
  pb_sku_label_art_ready: ["art_department", "product_handler"],
  pb_production_run: ["polybag_operator"],
  pb_quality_check: ["product_handler", "quality_control"],
  pb_packing: ["product_handler", "quality_control"],
  pb_complete: ["product_handler", "quality_control"],
};

/**
 * Fields set during Zoho CRM sync (importParsedPO).
 * When a work order has a non-null zohoId, these fields are read-only
 * in the UI and stripped from PATCH requests on the server.
 */
export const ZOHO_MANAGED_FIELDS = [
  "orderNumber",
  "customerName",
  "accountManager",
  "whenComplete",
  "shippingStreet",
  "shippingCity",
  "shippingState",
  "shippingZip",
  "incomingVendorProduct",
  "totalProductQuantity",
  "decorationType",
  "grandTotal",
  "specialInstructions",
  "workdriveUrl",
  "vendorNumber",
  "shipDate",
  "productName",
] as const;

// ─── Art Requests ────────────────────────────────────────────

export const ART_REQUEST_STATUSES = ["new", "in_progress", "complete"] as const;

export const artRequests = pgTable("art_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  poNumber: text("po_number").notNull(),
  vendor: text("vendor").notNull(),
  shipDate: timestamp("ship_date").notNull(),
  salesRep: text("sales_rep").notNull(),
  customer: text("customer").notNull(),
  status: text("status").notNull().default("new"),
  assignedTo: text("assigned_to"),
  files: jsonb("files").notNull().default(sql`'[]'::jsonb`).$type<{ fileName: string; downloadUrl: string }[]>(),
  workdriveFolderId: text("workdrive_folder_id"),
  workdriveFolderUrl: text("workdrive_folder_url"),
  zohoPoId: text("zoho_po_id"),
  notes: text("notes"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  completedAt: timestamp("completed_at"),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  check("art_requests_status_check", sql`${table.status} IN ('new', 'in_progress', 'complete')`),
  index("idx_art_requests_status").on(table.status),
  index("idx_art_requests_po_number").on(table.poNumber),
  index("idx_art_requests_assigned_to").on(table.assignedTo),
]);

export const insertArtRequestSchema = createInsertSchema(artRequests).omit({
  id: true, version: true, createdAt: true, updatedAt: true, completedAt: true, deletedAt: true,
});
export type InsertArtRequest = z.infer<typeof insertArtRequestSchema>;
export type ArtRequest = typeof artRequests.$inferSelect;

// === SHIPPING & RECEIVING ===

export const SHIPMENT_STATUSES = ["pending", "partially_received", "received"] as const;

export const inboundShipments = pgTable("inbound_shipments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackingNumber: text("tracking_number").notNull(),
  carrier: text("carrier"),
  vendorName: text("vendor_name"),
  zohoPoId: text("zoho_po_id"),
  poNumber: text("po_number"),
  poRootNumber: text("po_root_number"),
  workOrderId: varchar("work_order_id").references(() => workOrders.id, { onDelete: "set null" }),
  routingDestination: text("routing_destination"),
  status: text("status").notNull().default("pending"),
  expectedBoxes: integer("expected_boxes"),
  boxesReceived: integer("boxes_received").notNull().default(0),
  receivedAt: timestamp("received_at"),
  receivedBy: text("received_by"),
  notes: text("notes"),
  estimatedDeliveryAt: timestamp("estimated_delivery_at"),
  easypostTrackerId: text("easypost_tracker_id"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  uniqueIndex("idx_inbound_shipments_tracking_unique").on(table.trackingNumber),
  index("idx_inbound_shipments_po_root").on(table.poRootNumber),
  index("idx_inbound_shipments_wo_id").on(table.workOrderId),
  index("idx_inbound_shipments_status").on(table.status).where(sql`deleted_at IS NULL`),
  index("idx_inbound_shipments_created_at").on(table.createdAt),
  check("inbound_shipments_status_check", sql`${table.status} IN ('pending', 'partially_received', 'received')`),
]);

export const insertInboundShipmentSchema = createInsertSchema(inboundShipments).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertInboundShipment = z.infer<typeof insertInboundShipmentSchema>;
export type InboundShipment = typeof inboundShipments.$inferSelect;

export const receivingScanLog = pgTable("receiving_scan_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  rawScan: text("raw_scan").notNull(),
  normalizedTracking: text("normalized_tracking"),
  inboundShipmentId: varchar("inbound_shipment_id").references(() => inboundShipments.id, { onDelete: "set null" }),
  workOrderId: varchar("work_order_id").references(() => workOrders.id, { onDelete: "set null" }),
  scannedBy: text("scanned_by"),
  outcome: text("outcome").notNull(),
  routingDestination: text("routing_destination"),
  labelPrinted: boolean("label_printed").notNull().default(false),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
}, (table) => [
  index("idx_scan_log_shipment_id").on(table.inboundShipmentId),
  index("idx_scan_log_wo_id").on(table.workOrderId),
  index("idx_scan_log_created_at").on(table.createdAt),
]);

export const insertReceivingScanLogSchema = createInsertSchema(receivingScanLog).omit({ id: true, createdAt: true });
export type InsertReceivingScanLog = z.infer<typeof insertReceivingScanLogSchema>;
export type ReceivingScanLog = typeof receivingScanLog.$inferSelect;

export const PACKING_SESSION_STATUSES = ["in_progress", "packed_complete"] as const;

export const packingSessions = pgTable("packing_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workOrderId: varchar("work_order_id").notNull().references(() => workOrders.id, { onDelete: "cascade" }),
  linkedWorkOrderIds: varchar("linked_work_order_ids").array().notNull().default(sql`'{}'::varchar[]`),
  status: text("status").notNull().default("in_progress"),
  packedCompleteAt: timestamp("packed_complete_at"),
  archivedAt: timestamp("archived_at"),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  uniqueIndex("idx_packing_sessions_work_order_unique").on(table.workOrderId),
  index("idx_packing_sessions_archived_at").on(table.archivedAt),
  index("idx_packing_sessions_status").on(table.status).where(sql`deleted_at IS NULL`),
  check("packing_sessions_status_check", sql`${table.status} IN ('in_progress', 'packed_complete')`),
]);

export const packingSessionPallets = pgTable("packing_session_pallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packingSessionId: varchar("packing_session_id").notNull().references(() => packingSessions.id, { onDelete: "cascade" }),
  palletNumber: text("pallet_number").notNull(),
  notes: text("notes"),
  sortOrder: integer("sort_order").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_packing_session_pallets_session").on(table.packingSessionId),
]);

export const packingSessionBoxes = pgTable("packing_session_boxes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packingSessionId: varchar("packing_session_id").notNull().references(() => packingSessions.id, { onDelete: "cascade" }),
  palletId: varchar("pallet_id").references(() => packingSessionPallets.id, { onDelete: "set null" }),
  boxNumber: integer("box_number").notNull(),
  notes: text("notes"),
  labelPrintedAt: timestamp("label_printed_at"),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_packing_session_boxes_session").on(table.packingSessionId),
  index("idx_packing_session_boxes_pallet").on(table.palletId),
]);

export const packingSessionBoxItems = pgTable("packing_session_box_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  packingSessionBoxId: varchar("packing_session_box_id").notNull().references(() => packingSessionBoxes.id, { onDelete: "cascade" }),
  workOrderProductId: varchar("work_order_product_id").references(() => workOrderProducts.id, { onDelete: "set null" }),
  productName: text("product_name").notNull(),
  productDisplayName: text("product_display_name"),
  color: text("color").notNull(),
  size: text("size").notNull(),
  quantity: integer("quantity").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_packing_box_items_box").on(table.packingSessionBoxId),
  index("idx_packing_box_items_product").on(table.workOrderProductId),
]);

export const insertPackingSessionSchema = createInsertSchema(packingSessions).omit({
  id: true, version: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export const insertPackingSessionPalletSchema = createInsertSchema(packingSessionPallets).omit({
  id: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export const insertPackingSessionBoxSchema = createInsertSchema(packingSessionBoxes).omit({
  id: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export const insertPackingSessionBoxItemSchema = createInsertSchema(packingSessionBoxItems).omit({
  id: true, createdAt: true, updatedAt: true, deletedAt: true,
});
export type InsertPackingSession = z.infer<typeof insertPackingSessionSchema>;
export type PackingSession = typeof packingSessions.$inferSelect;
export type InsertPackingSessionPallet = z.infer<typeof insertPackingSessionPalletSchema>;
export type PackingSessionPallet = typeof packingSessionPallets.$inferSelect;
export type InsertPackingSessionBox = z.infer<typeof insertPackingSessionBoxSchema>;
export type PackingSessionBox = typeof packingSessionBoxes.$inferSelect;
export type InsertPackingSessionBoxItem = z.infer<typeof insertPackingSessionBoxItemSchema>;
export type PackingSessionBoxItem = typeof packingSessionBoxItems.$inferSelect;

export const adHocPackingLabels = pgTable("ad_hoc_packing_labels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recipientName: text("recipient_name").notNull(),
  method: text("method").notNull(),
  notes: text("notes"),
  generatedByUserId: varchar("generated_by_user_id").references(() => users.id, { onDelete: "set null" }),
  generatedBy: text("generated_by").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_ad_hoc_packing_labels_created_at").on(table.createdAt).where(sql`deleted_at IS NULL`),
  index("idx_ad_hoc_packing_labels_generated_by_user").on(table.generatedByUserId).where(sql`deleted_at IS NULL`),
]);

export const adHocPackingLabelItems = pgTable("ad_hoc_packing_label_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  adHocPackingLabelId: varchar("ad_hoc_packing_label_id").notNull().references(() => adHocPackingLabels.id, { onDelete: "cascade" }),
  productName: text("product_name").notNull(),
  quantity: integer("quantity").notNull(),
  sortOrder: integer("sort_order").notNull(),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_ad_hoc_packing_label_items_label").on(table.adHocPackingLabelId),
]);

export const insertAdHocPackingLabelSchema = createInsertSchema(adHocPackingLabels).omit({
  id: true, createdAt: true, deletedAt: true,
});
export const insertAdHocPackingLabelItemSchema = createInsertSchema(adHocPackingLabelItems).omit({
  id: true, createdAt: true, deletedAt: true,
});
export type InsertAdHocPackingLabel = z.infer<typeof insertAdHocPackingLabelSchema>;
export type AdHocPackingLabel = typeof adHocPackingLabels.$inferSelect;
export type InsertAdHocPackingLabelItem = z.infer<typeof insertAdHocPackingLabelItemSchema>;
export type AdHocPackingLabelItem = typeof adHocPackingLabelItems.$inferSelect;

// ── Zoho CRM Sync Tables ───────────────────────────────────────────────
// Read-only tables populated by Production Hub's CRM sync scheduler.
// Shared with Front Sales Agent (Pete) via the same Neon database.
// Table names match Pete's raw SQL queries exactly (no "crm_" prefix).

export const crmContacts = pgTable("contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").notNull().unique(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  email: text("email"),
  phone: text("phone"),
  mobile: text("mobile"),
  accountId: text("account_id"),
  ownerId: text("owner_id"),
  title: text("title"),
  department: text("department"),
  mailingCity: text("mailing_city"),
  mailingState: text("mailing_state"),
  contactType: text("contact_type"),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  syncedAt: timestamp("synced_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_contacts_email").on(table.email),
  index("idx_contacts_account_id").on(table.accountId),
  index("idx_contacts_active").on(table.email).where(sql`deleted_at IS NULL`),
]);

export const insertCrmContactSchema = createInsertSchema(crmContacts).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertCrmContact = z.infer<typeof insertCrmContactSchema>;
export type CrmContact = typeof crmContacts.$inferSelect;

export const crmAccounts = pgTable("accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").notNull().unique(),
  accountName: text("account_name").notNull(),
  industry: text("industry"),
  phone: text("phone"),
  website: text("website"),
  billingCity: text("billing_city"),
  billingState: text("billing_state"),
  ownerId: text("owner_id"),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  syncedAt: timestamp("synced_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_accounts_active").on(table.accountName).where(sql`deleted_at IS NULL`),
]);

export const insertCrmAccountSchema = createInsertSchema(crmAccounts).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertCrmAccount = z.infer<typeof insertCrmAccountSchema>;
export type CrmAccount = typeof crmAccounts.$inferSelect;

export const crmSalesOrders = pgTable("sales_orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").notNull().unique(),
  soNumber: text("so_number"),
  subject: text("subject"),
  contactId: text("contact_id"),
  accountId: text("account_id"),
  grandTotal: numeric("grand_total"),
  status: text("status"),
  createdTime: timestamp("created_time"),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  syncedAt: timestamp("synced_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_sales_orders_contact_id").on(table.contactId),
  index("idx_sales_orders_created_time").on(table.createdTime),
  index("idx_sales_orders_active_contact").on(table.contactId, table.createdTime).where(sql`deleted_at IS NULL`),
]);

export const insertCrmSalesOrderSchema = createInsertSchema(crmSalesOrders).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertCrmSalesOrder = z.infer<typeof insertCrmSalesOrderSchema>;
export type CrmSalesOrder = typeof crmSalesOrders.$inferSelect;

export const crmDeals = pgTable("deals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").notNull().unique(),
  dealName: text("deal_name"),
  stage: text("stage"),
  amount: numeric("amount"),
  contactId: text("contact_id"),
  accountId: text("account_id"),
  closingDate: timestamp("closing_date"),
  createdTime: timestamp("created_time"),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  syncedAt: timestamp("synced_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_deals_contact_id").on(table.contactId),
  index("idx_deals_created_time").on(table.createdTime),
  index("idx_deals_active_contact").on(table.contactId, table.createdTime).where(sql`deleted_at IS NULL`),
]);

export const insertCrmDealSchema = createInsertSchema(crmDeals).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertCrmDeal = z.infer<typeof insertCrmDealSchema>;
export type CrmDeal = typeof crmDeals.$inferSelect;

export const crmLeads = pgTable("leads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  zohoId: text("zoho_id").notNull().unique(),
  firstName: text("first_name"),
  lastName: text("last_name"),
  email: text("email"),
  phone: text("phone"),
  company: text("company"),
  title: text("title"),
  leadSource: text("lead_source"),
  leadStatus: text("lead_status"),
  industry: text("industry"),
  city: text("city"),
  state: text("state"),
  ownerId: text("owner_id"),
  zohoModifiedTime: timestamp("zoho_modified_time"),
  syncedAt: timestamp("synced_at").notNull().default(sql`now()`),
  createdAt: timestamp("created_at").notNull().default(sql`now()`),
  updatedAt: timestamp("updated_at").notNull().default(sql`now()`),
  deletedAt: timestamp("deleted_at"),
}, (table) => [
  index("idx_leads_email").on(table.email),
  index("idx_leads_active").on(table.email).where(sql`deleted_at IS NULL`),
]);

export const insertCrmLeadSchema = createInsertSchema(crmLeads).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true });
export type InsertCrmLead = z.infer<typeof insertCrmLeadSchema>;
export type CrmLead = typeof crmLeads.$inferSelect;
